import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/userState";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Button,
    Container,
    Grid,
    CircularProgress,
    FormHelperText,
} from "@mui/material";
import { styled } from "@mui/system";

const SectionCard = styled(Card)(({ theme }) => ({
    borderRadius: 10,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    backgroundColor: "#ffff",
    padding: theme.spacing(3),
    marginBottom: "1rem",
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(2),
    },
}));

const StyledInput = styled(TextField)(({ theme }) => ({
    width: "100%",
    "& .MuiInputBase-root": {
        backgroundColor: "#ffebee",
        borderRadius: 8,
        width: "100%",
        "&:hover": {
            backgroundColor: "#fff0f2",
        },
    },
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent",
    },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
    backgroundColor: "#ffebee",
    borderRadius: 8,
    "&:hover": {
        backgroundColor: "#fff0f2",
    },
}));

const MeasurementConfirmationPage = () => {
    const navigate = useNavigate();
    const { fetchGuidePdfByTemplate } = useUser();

    useEffect(() => {
        // Clear localStorage data for first_name, last_name, and other details on page load
        localStorage.removeItem("first_name");
        localStorage.removeItem("last_name");
        localStorage.removeItem("email");
        localStorage.removeItem("mobile");
        localStorage.removeItem("lash_length");
        localStorage.removeItem("curl_type");
        localStorage.removeItem("thickness");
        
    }, []);

    const [formData, setFormData] = useState({
        first_name: "",
        last_name: "",
        email: "",
        mobile: "",
        lash_length: "",
        curl_type: "",
        thickness: "",
    });

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        const storedData = {
            first_name: localStorage.getItem("first_name") || "",
            last_name: localStorage.getItem("last_name") || "",
            email: localStorage.getItem("email") || "",
            mobile: localStorage.getItem("mobile") || "",
            lash_length: localStorage.getItem("lash_length_mm") || "",
            curl_type: localStorage.getItem("curl_type") || "",
            thickness: localStorage.getItem("thickness") || "",
        };
        setFormData((prev) => ({ ...prev, ...storedData }));
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const validateFormData = () => {
        const errors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
        const mobileRegex = /^[0-9]{6,25}$/;

        if (!formData.first_name.trim()) {
            errors.first_name = "First Name is required";
        }

        if (!formData.last_name.trim()) {
            errors.last_name = "Last Name is required";
        }

        if (!formData.email.trim()) {
            errors.email = "Email is required";
        } else if (!emailRegex.test(formData.email.trim())) {
            errors.email = "Enter a valid email address";
        }

        if (!formData.mobile.trim()) {
            errors.mobile = "Mobile Number is required";
        } else if (!mobileRegex.test(formData.mobile.trim())) {
            errors.mobile = "Mobile Number must be 6–25 digits";
        }

        if (!formData.lash_length || !formData.lash_length.trim()) {
            errors.lash_length = "Lash Length is required";
        }

        if (!formData.curl_type || !formData.curl_type.trim()) {
            errors.curl_type = "Curl Type is required";
        }

        if (!formData.thickness || !formData.thickness.trim()) {
            errors.thickness = "Thickness is required";
        }

        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        const errors = validateFormData();
        setValidationErrors(errors);

        if (Object.keys(errors).length > 0) {
            console.warn("Validation failed:", errors);
            return;
        }

        setIsLoading(true);
        try {
            Object.entries(formData).forEach(([key, value]) => {
                localStorage.setItem(key, value);
            });

            const response = await new Promise((resolve) =>
                setTimeout(() => resolve({ ok: true }), 1000)
            );

            if (response.ok) {
                const selectedImageId = JSON.parse(localStorage.getItem("selectedImageId") || "[]");
                const templateId = selectedImageId;
                const lashSize = formData.lash_length;
                const pdfResponse = await fetchGuidePdfByTemplate(templateId, lashSize);

                navigate("/guide-pdf", {
                    state: {
                        pdfData: pdfResponse.status ? pdfResponse.data : null,
                        error: pdfResponse.status ? null : pdfResponse.error || "No guide found.",
                        templateId,
                        lashSize,
                    },
                });
            } else {
                setError("Failed to submit measurements.");
            }
        } catch (err) {
            console.error("Unexpected error:", err);
            setError("An unexpected error occurred.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container maxWidth="sm" sx={{ backgroundColor: "#ffebee", padding: 2, borderRadius: 2 }}>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                <SectionCard>
                    <CardContent>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#525050", mb: 3 }}>
                            Customer Info
                        </Typography>
                        {error && (
                            <Box sx={{ mb: 2 }}>
                                <Typography color="error" variant="body2">{error}</Typography>
                            </Box>
                        )}

                        <Grid container spacing={2} sx={{ flexDirection: "column" }}>
                            {[
                                { name: "first_name", label: "First Name" },
                                { name: "last_name", label: "Last Name" },
                                { name: "email", label: "Email", type: "email" },
                                { name: "mobile", label: "Mobile Number", type: "number" },
                            ].map((field) => (
                                <Grid item xs={12} key={field.name}>
                                    <Typography variant="body1" sx={{ mb: 1, color: "#555" }}>
                                        {field.label}
                                    </Typography>
                                    <StyledInput
                                        name={field.name}
                                        value={formData[field.name]}
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                        type={field.type || "text"}
                                        placeholder={`Enter ${field.label}`}
                                        error={Boolean(validationErrors[field.name])}
                                        helperText={validationErrors[field.name]}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </SectionCard>

                <SectionCard>
                    <CardContent>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#525050", mb: 3 }}>
                            Lash Measurements
                        </Typography>

                        <Grid container spacing={2} sx={{ flexDirection: "column" }}>
                            <Grid item xs={12}>
                                <Typography variant="body1" sx={{ mb: 1, color: "#555" }}>
                                   Middle Lash Length
                                </Typography>
                                <FormControl fullWidth error={Boolean(validationErrors["lash_length"])} disabled={isLoading}>
                                    <StyledSelect
                                        name="lash_length"
                                        value={formData["lash_length"]}
                                        onChange={handleInputChange}
                                        displayEmpty
                                    >
                                        <MenuItem value="">Select Lash Length</MenuItem>
                                        {["6", "7", "8", "9", "10"].map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </StyledSelect>
                                    <FormHelperText>{validationErrors["lash_length"]}</FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body1" sx={{ mb: 1, color: "#555" }}>
                                    Curl Type
                                </Typography>
                                <FormControl fullWidth error={Boolean(validationErrors["curl_type"])} disabled={isLoading}>
                                    <StyledSelect
                                        name="curl_type"
                                        value={formData["curl_type"]}
                                        onChange={handleInputChange}
                                        displayEmpty
                                    >
                                        <MenuItem value="">Select Curl Type</MenuItem>
                                        {["Straight or Semi Curved", "Curved", "Curly"].map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </StyledSelect>
                                    <FormHelperText>{validationErrors["curl_type"]}</FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body1" sx={{ mb: 1, color: "#555" }}>
                                    Thickness
                                </Typography>
                                <FormControl fullWidth error={Boolean(validationErrors["thickness"])} disabled={isLoading}>
                                    <StyledSelect
                                        name="thickness"
                                        value={formData["thickness"]}
                                        onChange={handleInputChange}
                                        displayEmpty
                                    >
                                        <MenuItem value="">Select Thickness</MenuItem>
                                        {["0.10 mm", "0.15 mm"].map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </StyledSelect>
                                    <FormHelperText>{validationErrors["thickness"]}</FormHelperText>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                </SectionCard>

                <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
                    <Button
                        type="submit"
                        variant="contained"
                        sx={{
                            backgroundColor: "#B1727C",
                            "&:hover": { backgroundColor: "#8a5c64" },
                            color: "#fff",
                            px: 4,
                            py: 1.5,
                            fontSize: "1rem",
                            borderRadius: 20,
                        }}
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isLoading ? "Submitting..." : "Submit Measurement"}
                    </Button>
                </Box>
            </Box>
        </Container>
    );
};

export default MeasurementConfirmationPage;
